import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  TEST_CONFIGURATION,
  TEST_OVERVIEW,
  TEST_MANAGEMENT_TESTS,
  DESTINATIONS,
} from '@elementary/common-ui/constants/queryKeys';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import useSnackbar from 'shared/hooks/useSnackbar';
import * as api from 'shared/services/api';
import { TestManagementGqlTestsResponse } from 'shared/types';

const useTestNotificationsSettings = () => {
  const { openSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const selectedEnvId = useSelectedEnv();

  const invalidateQueries = (ids: string[], muted: boolean) => {
    ids.forEach((id) => {
      queryClient.invalidateQueries([TEST_CONFIGURATION, id]);
      queryClient.invalidateQueries([TEST_OVERVIEW, id]);
    });

    queryClient.setQueryData(
      [TEST_MANAGEMENT_TESTS, selectedEnvId, DESTINATIONS],
      (old?: TestManagementGqlTestsResponse) => ({
        tests:
          old?.tests.map((test) => {
            if (ids.includes(test.id)) {
              return {
                ...test,
                config: {
                  ...test.config,
                  muted,
                },
              };
            }
            return test;
          }) || [],
      }),
    );
  };

  const { mutateAsync: muteTests, isLoading: isMuteTestsLoading } = useMutation(
    (ids: string[]) => api.editTestsNotification(selectedEnvId!, ids, true),
    {
      onSuccess: (_, ids) => {
        openSnackbar({
          message: 'Alerts disabled for the selected tests.',
          variant: 'success',
        });
        invalidateQueries(ids, true);
      },

      onError: () =>
        openSnackbar({
          message: 'Failed to update notification settings',
          variant: 'error',
        }),
    },
  );

  const { mutateAsync: unMuteTests, isLoading: isUnMuteTestsLoading } =
    useMutation(
      (ids: string[]) => api.editTestsNotification(selectedEnvId!, ids, false),
      {
        onSuccess: (_, ids) => {
          openSnackbar({
            message: 'Alerts enabled for the selected tests.',
            variant: 'success',
          });
          invalidateQueries(ids, false);
        },
        onError: () =>
          openSnackbar({
            message: 'Failed to update notification settings',
            variant: 'error',
          }),
      },
    );

  return {
    isMuteTestsLoading,
    isUnMuteTestsLoading,
    muteTests,
    unMuteTests,
  };
};

export default useTestNotificationsSettings;
